import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import ScrollableTextarea from "@/common/ScrollableTextarea";
import CloseIcon from "@mui/icons-material/Close";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { toast } from "react-toastify";
import { ReportRounded } from "@mui/icons-material";
import { useVisualViewport } from "@/hooks/useVisualViewport";
import { getApiErrorMessage } from "@/utils/helper";

interface ReportIssueModalProps {
  open: boolean;
  onClose: () => void;
  contributionId: number | string;
}

const styles = {
  dialogPaper: {
    width: { xs: "90%", sm: 400 },
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    p: { xs: "2%", sm: "4%" },
    outline: "none",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    textAlign: "center" as const,
    overflowY: "auto",
    position: "relative" as const,
  },
  closeButton: {
    position: "absolute",
    right: "2%",
    top: "2%",
    color: "#9e9e9e",
    zIndex: 10,
  },
  reportIcon: {
    color: "#8B5CF6",
    height: { xs: "19.42vw", sm: 80 },
    width: { xs: "19.42vw", sm: 80 },
  },
  title: {
    fontSize: { xs: "3.88vw", sm: 16 },
    fontWeight: 700,
    mb: "1%",
    color: "#000",
  },
  description: { fontSize: { xs: "3.16vw", sm: 13 }, color: "#666", mb: "4%" },
  buttonGroup: { display: "flex", gap: "2%", width: "100%", mt: "5%" },
  cancelButton: {
    bgcolor: "#F3E8FF",
    color: "#313131",
    fontSize: { xs: "3.88vw", sm: 16 },
    fontWeight: 700,
    borderRadius: "30px",
    "&:hover": { bgcolor: "#e9d5ff" },
  },
  submitButton: {
    bgcolor: "#8B5CF6",
    color: "#fff",
    fontSize: { xs: "3.88vw", sm: 16 },
    fontWeight: 700,
    borderRadius: "30px",
    "&:hover": { bgcolor: "#7c3aed" },
  },
};

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  open,
  onClose,
  contributionId,
}) => {
  const { api } = useApiClient();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const dialogContentRef = useRef<HTMLDivElement | null>(null);

  useVisualViewport(open, dialogContentRef, paperRef);

  const handleSubmit = async () => {
    try {
      if (!reason) {
        toast.error("Please enter a reason");
        return;
      }
      if (!contributionId) {
        toast.error("Invalid contribution ID");
        return;
      }

      setLoading(true);

      await api.post(ENDPOINTS.REPORT.CREATE(Number(contributionId)), {
        reason,
      });

      toast.success(
        <span>
          <strong style={{ fontWeight: 800 }}>Report Submitted!</strong> We'll
          look into it right away.
        </span>,
      );
      setReason("");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(getApiErrorMessage(error, "Something went wrong"), {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const modalOnClose = loading ? () => {} : onClose;

  return (
    <Dialog
      open={open}
      onClose={modalOnClose}
      scroll="paper"
      PaperProps={{
        ref: paperRef,
        sx: {
          ...styles.dialogPaper,
          transition: "transform 160ms ease",
          transform: "",
        },
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogContent
        ref={dialogContentRef}
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          onClick={onClose}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          sx={{
            ...styles.closeButton,
            touchAction: "manipulation",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
          }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            p: { xs: 4, sm: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <ReportRounded sx={styles.reportIcon} />

          <Typography variant="h6" sx={styles.title}>
            Report an Issue
          </Typography>

          <Typography variant="body2" sx={styles.description}>
            Please let us know the reason for the report below.
          </Typography>

          <ScrollableTextarea
            value={reason}
            onChange={setReason}
            placeholder="Briefly describe the issue"
            disabled={loading}
          />

          <Box sx={styles.buttonGroup}>
            <Button
              fullWidth
              onClick={onClose}
              sx={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              fullWidth
              onClick={handleSubmit}
              sx={{
                ...styles.submitButton,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={18} color="inherit" />
                  <span>Submitting...</span>
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportIssueModal;
